import React from 'react';
import {
  TrendingUp,
  BarChart3,
  PieChart,
  Clock,
  CheckCircle2,
  Calendar,
  Users,
  Briefcase,
  Layers,
  Award
} from 'lucide-react';
import { PracticeAnalyticsSummary } from '../../types/practiceManagement';
import { Language } from '../../types/kundali';
import { getSavedAstrologerBranding } from '../../lib/astrologerBrandingService';

interface PracticeAnalyticsProps {
  analytics: PracticeAnalyticsSummary;
  lang: Language;
}

export const PracticeAnalytics: React.FC<PracticeAnalyticsProps> = ({
  analytics,
  lang
}) => {
  const isHi = lang === 'hi';
  const branding = getSavedAstrologerBranding();

  const stagesList = [
    { key: 'NEW', label: 'New Cases', count: analytics.pipelineDistribution.NEW || 0, color: 'bg-blue-500' },
    { key: 'ANALYSIS', label: 'In Analysis', count: analytics.pipelineDistribution.ANALYSIS || 0, color: 'bg-astro-secondary-surface0' },
    { key: 'REVIEW', label: 'Review Stage', count: analytics.pipelineDistribution.REVIEW || 0, color: 'bg-purple-500' },
    { key: 'CONSULTATION', label: 'Consultation', count: analytics.pipelineDistribution.CONSULTATION || 0, color: 'bg-orange-500' },
    { key: 'REPORT_PREP', label: 'Report Prep', count: analytics.pipelineDistribution.REPORT_PREP || 0, color: 'bg-indigo-500' },
    { key: 'DELIVERED', label: 'Delivered', count: analytics.pipelineDistribution.DELIVERED || 0, color: 'bg-emerald-500' },
    { key: 'FOLLOW_UP', label: 'Follow-Up Due', count: analytics.pipelineDistribution.FOLLOW_UP || 0, color: 'bg-teal-500' }
  ];

  return (
    <div className="space-y-4">
      {/* Top Header */}
      <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-lg bg-blue-500/15 text-blue-600 dark:text-blue-400">
            <BarChart3 className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-sm font-black text-astro-text dark:text-astro-muted">
              {isHi ? 'ज्योतिष अभ्यास परिचालन विश्लेषण' : 'Practice Operations & Analytical Velocity'}
            </h2>
            <p className="text-xs text-astro-muted">
              {isHi
                ? 'केस प्रवाह, टर्नअराउंड समय, परामर्श मात्रा एवं विषय वितरण'
                : 'Turnaround benchmarks, case pipeline distribution, and consultation efficiency'}
            </p>
          </div>
        </div>

        <span className="text-xs font-mono text-astro-muted">
          Period: Monthly Rolling
        </span>
      </div>

      {/* Grid of Key Analytical Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3.5">
        <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs space-y-1">
          <span className="text-xs text-astro-muted font-semibold">Total Active Client Base</span>
          <div className="text-2xl font-black text-astro-text dark:text-astro-muted">
            {analytics.totalClients}
          </div>
          <p className="text-[11px] text-emerald-600 font-semibold">
            High retention & longitudinal continuity
          </p>
        </div>

        <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs space-y-1">
          <span className="text-xs text-astro-muted font-semibold">Avg Report Turnaround</span>
          <div className="text-2xl font-black text-astro-text dark:text-astro-muted">
            {analytics.averageReportTurnaroundDays} days
          </div>
          <p className="text-[11px] text-astro-muted">
            From case intake to verified delivery
          </p>
        </div>

        <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs space-y-1">
          <span className="text-xs text-astro-muted font-semibold">Follow-Up Completion</span>
          <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400">
            {analytics.followUpsCompletedRate}%
          </div>
          <p className="text-[11px] text-astro-muted">
            Timely client review compliance
          </p>
        </div>

        <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs space-y-1">
          <span className="text-xs text-astro-muted font-semibold">Monthly Consultations</span>
          <div className="text-2xl font-black text-amber-600 dark:text-amber-400">
            {analytics.consultationsConducted}
          </div>
          <p className="text-[11px] text-astro-muted">
            Completed in current billing cycle
          </p>
        </div>
      </div>

      {/* Pipeline Distribution Bar Chart */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-slate-850 p-5 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-astro-muted flex items-center gap-2">
            <Layers className="w-4 h-4 text-amber-500" />
            <span>Case Pipeline Distribution</span>
          </h3>

          <div className="space-y-2.5 pt-2">
            {stagesList.map((s) => (
              <div key={s.key} className="space-y-1">
                <div className="flex justify-between text-xs font-bold text-astro-secondary-text dark:text-astro-muted">
                  <span>{s.label}</span>
                  <span>{s.count} cases</span>
                </div>
                <div className="w-full h-2 rounded-full bg-astro-secondary-surface dark:bg-astro-secondary overflow-hidden">
                  <div
                    className={`h-full ${s.color} rounded-full`}
                    style={{ width: `${Math.min(100, s.count * 20)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Most Requested Life Themes */}
        <div className="bg-white dark:bg-slate-850 p-5 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-astro-muted flex items-center gap-2">
            <PieChart className="w-4 h-4 text-amber-500" />
            <span>Most Inquired Life Themes</span>
          </h3>

          <div className="space-y-2.5 pt-2">
            {analytics.mostRequestedThemes.map((th, idx) => (
              <div
                key={th.themeKey}
                className="p-3 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border flex items-center justify-between text-xs"
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-amber-600">0{idx + 1}</span>
                  <span className="font-bold text-astro-text dark:text-astro-muted">
                    {th.themeKey.replace(/_/g, ' ')}
                  </span>
                </div>
                <span className="font-bold text-astro-text dark:text-astro-muted bg-white dark:bg-astro-secondary px-2 py-0.5 rounded border border-astro-border dark:border-astro-strong-border">
                  {th.count} inquiries
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Astrologer Kundali Creation Audit Card */}
      <div className="bg-white dark:bg-slate-850 p-5 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs space-y-3">
        <div className="flex items-center justify-between border-b border-astro-border dark:border-astro-strong-border pb-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-astro-muted flex items-center gap-2">
            <Users className="w-4 h-4 text-[#C82A1E]" />
            <span>{isHi ? 'ज्योतिषी कुण्डली निर्माण उत्पादकता' : 'Astrologer Kundali Creation Volume'}</span>
          </h3>
          <span className="text-[11px] font-semibold text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded border border-emerald-300">
            Active Management Audit
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
          <div className="p-3 bg-stone-50 dark:bg-slate-900 rounded-lg border border-astro-border space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="font-bold text-astro-text">{branding.name || (isHi ? 'मुख्य परामर्शदाता' : 'Lead Consultant')}</span>
              <span className="font-bold font-mono text-[#C82A1E]">24 Charts</span>
            </div>
            <div className="w-full bg-stone-200 dark:bg-slate-700 h-1.5 rounded-full overflow-hidden">
              <div className="bg-[#C82A1E] h-full rounded-full" style={{ width: '44%' }} />
            </div>
            <span className="text-[10px] text-stone-500 block">{branding.sansthan || (isHi ? 'वैदिक परामर्श संस्थान' : 'Vedic Astrological Practice')}</span>
          </div>

          <div className="p-3 bg-stone-50 dark:bg-slate-900 rounded-lg border border-astro-border space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="font-bold text-astro-text">{isHi ? 'सहयोगी ज्योतिषाचार्य' : 'Associate Consultant'}</span>
              <span className="font-bold font-mono text-amber-700">18 Charts</span>
            </div>
            <div className="w-full bg-stone-200 dark:bg-slate-700 h-1.5 rounded-full overflow-hidden">
              <div className="bg-amber-600 h-full rounded-full" style={{ width: '33%' }} />
            </div>
            <span className="text-[10px] text-stone-500 block">{isHi ? 'अनुसंधान शाखा' : 'Vedic Research Wing'}</span>
          </div>

          <div className="p-3 bg-stone-50 dark:bg-slate-900 rounded-lg border border-astro-border space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="font-bold text-astro-text">{isHi ? 'परामर्श सहायक' : 'Junior Consultant'}</span>
              <span className="font-bold font-mono text-blue-700">12 Charts</span>
            </div>
            <div className="w-full bg-stone-200 dark:bg-slate-700 h-1.5 rounded-full overflow-hidden">
              <div className="bg-blue-600 h-full rounded-full" style={{ width: '23%' }} />
            </div>
            <span className="text-[10px] text-stone-500 block">{isHi ? 'शास्त्रीय केंद्र' : 'Vedic Desk'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
